import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, Gem, Palette, Eye, Scissors, CircleDot } from "lucide-react";
import { api } from "@/lib/api";
import SolitaireCard from "@/components/SolitaireCard";
import SolitaireModal from "@/components/SolitaireModal";

const SHAPES = ["ROUND", "PEAR", "OVAL", "EMERALD", "PRINCESS", "HEART", "CUSHION", "RADIANT", "MARQUISE", "ASSCHER"];
const COLOURS = ["D", "E", "F", "G", "H", "I", "J", "K", "L"];
const CLARITIES = ["FL", "IF", "VVS1", "VVS2", "VS1", "VS2", "SI1", "SI2", "SI3", "I1"];
const LABS = ["IGI", "GIA", "HRD"];

const SORT_OPTIONS = [
  { v: "carat_desc", label: "Largest first" },
  { v: "carat_asc", label: "Smallest first" },
  { v: "newest", label: "Newest first" },
];

// Carat bucket multi-select. Each label maps to a [min, max) inclusive of min, exclusive of max.
const CARAT_BUCKETS = [
  { key: "0-1", label: "0–1 ct", min: 0,    max: 1 },
  { key: "1-2", label: "1–2 ct", min: 1,    max: 2 },
  { key: "2-3", label: "2–3 ct", min: 2,    max: 3 },
  { key: "3-4", label: "3–4 ct", min: 3,    max: 4 },
  { key: "4-5", label: "4–5 ct", min: 4,    max: 5 },
  { key: "5-6", label: "5–6 ct", min: 5,    max: 6 },
  { key: "6-7", label: "6–7 ct", min: 6,    max: 7 },
];

export default function Solitaires() {
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const shape = params.get("shape") || "";
  const colour = params.get("colour") || "";
  const clarity = params.get("clarity") || "";
  const cert_lab = params.get("cert_lab") || "";
  const q = params.get("q") || "";
  const sort = params.get("sort") || "carat_desc";
  // Carat buckets stored as comma-separated keys in the URL (e.g. carat=1-2,2-3)
  const caratBucketsParam = params.get("carat") || "";
  const selectedCaratKeys = useMemo(
    () => caratBucketsParam.split(",").map((s) => s.trim()).filter(Boolean),
    [caratBucketsParam]
  );

  useEffect(() => {
    setLoading(true);
    const qs = new URLSearchParams();
    if (shape) qs.set("shape", shape);
    if (colour) qs.set("colour", colour);
    if (clarity) qs.set("clarity", clarity);
    if (cert_lab) qs.set("cert_lab", cert_lab);
    if (q) qs.set("q", q);
    if (sort) qs.set("sort", sort);
    qs.set("limit", "120");
    api.get(`/solitaires?${qs.toString()}`)
      .then(({ data }) => { setItems(data.items || []); setTotal(data.total || 0); })
      .catch(() => { setItems([]); setTotal(0); })
      .finally(() => setLoading(false));
  }, [shape, colour, clarity, cert_lab, q, sort]);

  const setParam = (k, v) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v); else next.delete(k);
    setParams(next);
  };

  const toggleCaratBucket = (key) => {
    const set = new Set(selectedCaratKeys);
    if (set.has(key)) set.delete(key); else set.add(key);
    const ordered = CARAT_BUCKETS.map((b) => b.key).filter((k) => set.has(k));
    const next = new URLSearchParams(params);
    if (ordered.length) next.set("carat", ordered.join(",")); else next.delete("carat");
    setParams(next);
  };

  // Client-side carat filter for instant updates (no backend roundtrip).
  const filteredItems = useMemo(() => {
    if (selectedCaratKeys.length === 0) return items;
    const ranges = CARAT_BUCKETS.filter((b) => selectedCaratKeys.includes(b.key));
    return items.filter((it) => {
      const c = Number(it.carat || 0);
      return ranges.some((r) => c >= r.min && c < r.max);
    });
  }, [items, selectedCaratKeys]);

  const headerInfo = useMemo(() => {
    if (loading) return "Loading…";
    const n = filteredItems.length;
    if (selectedCaratKeys.length > 0) {
      return `${n} of ${total} certified solitaire${total === 1 ? "" : "s"}`;
    }
    return `${total} certified solitaire${total === 1 ? "" : "s"}`;
  }, [loading, total, filteredItems.length, selectedCaratKeys.length]);

  const hasAnyFilter = shape || colour || clarity || cert_lab || q || selectedCaratKeys.length > 0;

  return (
    <div className="bg-white">
      {/* Page header */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-12 pb-8">
        <div className="ornament mb-3" style={{ color: "#A88934" }}>Loose Stones</div>
        <h1 className="font-display leading-tight" style={{ fontSize: "clamp(2rem, 4vw, 3.5rem)", color: "#0B1A33", fontWeight: 400 }}>
          Solitaires <em style={{ fontStyle: "italic", color: "#A88934" }}>— hand-picked, certified, transparently priced.</em>
        </h1>
        <p className="mt-4 max-w-2xl text-base leading-relaxed" style={{ color: "rgba(11,26,51,0.7)" }}>
          Every diamond below is laboratory-certified (IGI · GIA · HRD) with its own certificate number, 360° view
          and complete grading. Prices update live with today&apos;s USD rate.
        </p>
      </section>

      {/* Filters */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-4">
        <div className="flex flex-wrap items-center gap-3 p-4 border" style={{ borderColor: "rgba(201,168,76,0.25)", background: "#FAF7EE" }}>
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: "rgba(11,26,51,0.5)" }} />
            <input
              type="text"
              placeholder="Search by Stock ID or Certificate No."
              defaultValue={q}
              onChange={(e) => setParam("q", e.target.value)}
              data-testid="solitaire-search"
              className="w-full pl-10 pr-3 py-2.5 text-sm bg-white border focus:outline-none"
              style={{ borderColor: "rgba(11,26,51,0.2)", color: "#0B1A33" }}
            />
          </div>
          <Select label="Shape" value={shape} options={SHAPES} onChange={(v) => setParam("shape", v)} testId="solitaire-filter-shape" />
          <Select label="Colour" value={colour} options={COLOURS} onChange={(v) => setParam("colour", v)} testId="solitaire-filter-colour" />
          <Select label="Clarity" value={clarity} options={CLARITIES} onChange={(v) => setParam("clarity", v)} testId="solitaire-filter-clarity" />
          <Select label="Lab" value={cert_lab} options={LABS} onChange={(v) => setParam("cert_lab", v)} testId="solitaire-filter-lab" />
          <Select label="Sort" value={sort} options={SORT_OPTIONS} onChange={(v) => setParam("sort", v || "carat_desc")} testId="solitaire-sort" plainValueKey />
          {hasAnyFilter && (
            <button onClick={() => setParams(new URLSearchParams())} className="text-[11px] tracking-widest uppercase underline" style={{ color: "#A88934" }} data-testid="solitaire-reset">
              Reset
            </button>
          )}
        </div>

        {/* Carat bucket multi-select */}
        <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="solitaire-carat-filter">
          <span className="text-[10px] tracking-[0.3em] uppercase mr-1" style={{ color: "rgba(11,26,51,0.55)" }}>Carat</span>
          {CARAT_BUCKETS.map((b) => {
            const active = selectedCaratKeys.includes(b.key);
            return (
              <button
                key={b.key}
                type="button"
                onClick={() => toggleCaratBucket(b.key)}
                data-testid={`solitaire-carat-${b.key}`}
                aria-pressed={active}
                className="px-3 py-1.5 text-[11px] tracking-widest uppercase border transition-colors"
                style={{
                  background: active ? "#0B1A33" : "#FFFFFF",
                  color: active ? "#FAF6EE" : "#0B1A33",
                  borderColor: active ? "#0B1A33" : "rgba(11,26,51,0.2)",
                }}
              >
                {b.label}
              </button>
            );
          })}
        </div>

        <div className="text-[11px] tracking-[0.25em] uppercase mt-3" style={{ color: "rgba(11,26,51,0.55)" }}>{headerInfo}</div>
      </section>

      {/* Grid */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-16">
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={`skel-${i}`} className="aspect-square border animate-pulse" style={{ background: "#F5F0E2", borderColor: "rgba(201,168,76,0.2)" }} />
            ))}
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="text-center py-20" style={{ color: "rgba(11,26,51,0.6)" }}>
            <Gem className="w-12 h-12 mx-auto mb-4" style={{ color: "#C9A84C" }} strokeWidth={0.8} />
            <p className="text-lg font-display">No solitaires match these filters.</p>
            <p className="text-sm mt-2">Try resetting filters or broadening the search.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
            {filteredItems.map((it, i) => (
              <SolitaireCard key={it.stock_id} item={it} onOpen={setSelected} index={i} />
            ))}
          </div>
        )}
      </section>

      {/* 4C's education section */}
      <FourCsSection />

      {selected && <SolitaireModal item={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}

const Select = ({ label, value, options, onChange, testId, plainValueKey }) => (
  <select
    value={value}
    onChange={(e) => onChange(e.target.value)}
    data-testid={testId}
    className="px-3 py-2.5 text-sm bg-white border focus:outline-none cursor-pointer"
    style={{ borderColor: "rgba(11,26,51,0.2)", color: "#0B1A33", minWidth: 120 }}
  >
    <option value="">{label}</option>
    {options.map((o) => {
      if (typeof o === "string") return <option key={o} value={o}>{o}</option>;
      const k = plainValueKey ? o.v : o.v;
      return <option key={k} value={k}>{o.label}</option>;
    })}
  </select>
);

// Minimal editorial 4C's row — explains the grading vocabulary used above.
const FOUR_CS = [
  {
    key: "cut",
    label: "Cut",
    icon: Scissors,
    body: "How a diamond's facets interact with light. Excellent and Ideal cuts return maximum brilliance.",
  },
  {
    key: "colour",
    label: "Colour",
    icon: Palette,
    body: "Graded D (icy-white, rarest) through Z. The closer to D, the more colourless and rare the stone.",
  },
  {
    key: "clarity",
    label: "Clarity",
    icon: Eye,
    body: "Inclusions visible under 10× magnification. FL is flawless; VS and SI offer beauty at value.",
  },
  {
    key: "carat",
    label: "Carat",
    icon: CircleDot,
    body: "The weight of a diamond. One carat equals 0.2 grams — though size alone does not define beauty.",
  },
];

function FourCsSection() {
  return (
    <section className="border-t" style={{ borderColor: "rgba(201,168,76,0.25)", background: "#FAF7EE" }} data-testid="four-cs-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        <div className="text-center mb-10">
          <div className="ornament" style={{ color: "#A88934" }}>The Vocabulary</div>
          <h2 className="font-display mt-3 leading-tight" style={{ fontSize: "clamp(1.75rem, 3vw, 2.5rem)", color: "#0B1A33", fontWeight: 400 }}>
            Reading a diamond — <em style={{ fontStyle: "italic", color: "#A88934" }}>the four C&apos;s.</em>
          </h2>
          <p className="mt-3 max-w-2xl mx-auto text-sm leading-relaxed" style={{ color: "rgba(11,26,51,0.65)" }}>
            Every stone in our collection is graded against these four universal benchmarks, certified
            independently by IGI, GIA or HRD.
          </p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          {FOUR_CS.map(({ key, label, icon: Icon, body }) => (
            <div
              key={key}
              className="bg-white border p-5 md:p-6 transition-colors hover:border-gold"
              style={{ borderColor: "rgba(201,168,76,0.3)" }}
              data-testid={`four-cs-${key}`}
            >
              <div className="w-10 h-10 flex items-center justify-center border mb-4" style={{ borderColor: "rgba(168,137,52,0.5)", color: "#A88934" }}>
                <Icon className="w-4 h-4" strokeWidth={1.5} />
              </div>
              <div className="overline" style={{ color: "#A88934" }}>{label}</div>
              <p className="mt-2 text-sm leading-relaxed" style={{ color: "rgba(11,26,51,0.75)" }}>
                {body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
